import { useEffect, useRef, useState, type DragEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { uploadMediaFile } from '@/hooks/useUpload';
import { listZCreations, listZProducts, firstProductImage, type ZStudioCreation, type ZProductItem } from '@/lib/zstudio';

type Tab = 'upload' | 'creations' | 'products';

/**
 * Sélecteur d'image réutilisable : téléversement, réutilisation d'une création
 * ZStudio, ou image d'un produit Zifek. Renvoie l'URL sélectionnée.
 */
export default function ZImagePicker({
  value,
  onChange,
  label = 'Image',
  hint,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  label?: string;
  hint?: string;
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('upload');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creations, setCreations] = useState<ZStudioCreation[]>([]);
  const [products, setProducts] = useState<ZProductItem[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (tab === 'creations' && creations.length === 0 && user?.id) {
      listZCreations(user.id).then((c) => setCreations(c.filter((x) => x.media_kind === 'image' && x.url))).catch(() => {});
    }
    if (tab === 'products' && products.length === 0) {
      listZProducts().then(setProducts).catch(() => {});
    }
  }, [open, tab, user?.id, creations.length, products.length]);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const url = await uploadMediaFile(file, 'zstudio/inputs');
      onChange(url);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Téléversement impossible.');
    } finally {
      setBusy(false);
    }
  };

  // Glisser-déposer une image directement sur le sélecteur (ou dans la modale).
  const onDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) void handleFile(file);
    else if (file) setError('Seules les images sont acceptées.');
  };

  const dragProps = {
    onDragOver: (e: DragEvent<HTMLElement>) => { e.preventDefault(); setDragging(true); },
    onDragLeave: () => setDragging(false),
    onDrop,
  };

  return (
    <div>
      <p className="text-xs font-medium text-foreground-600 mb-1.5">{label}</p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        {...dragProps}
        className={`w-full aspect-[4/3] rounded-xl border overflow-hidden flex flex-col items-center justify-center transition-colors cursor-pointer ${dragging ? 'border-primary-400 bg-primary-50/60' : value ? 'border-background-200/70 bg-background-100' : 'border-dashed border-background-300/70 bg-background-50 hover:border-primary-300'}`}
      >
        {busy ? (
          <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
        ) : value ? (
          <img src={value} alt={label} className="w-full h-full object-cover object-top" />
        ) : (
          <>
            <span className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center mb-2">
              <i className={`${dragging ? 'ri-download-2-line text-primary-600' : 'ri-image-add-line text-foreground-400'} text-lg`}></i>
            </span>
            <span className="text-xs text-foreground-500">{dragging ? 'Déposez l’image ici' : 'Glisser-déposer ou cliquer'}</span>
          </>
        )}
      </button>
      {value && (
        <button type="button" onClick={() => onChange(null)} className="mt-1.5 text-[11px] text-foreground-400 hover:text-red-500 transition-colors cursor-pointer">
          <i className="ri-close-line mr-0.5"></i>Retirer
        </button>
      )}
      {hint && <p className="text-[11px] text-foreground-400 mt-1.5">{hint}</p>}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70">
              <h3 className="text-sm font-bold text-foreground-950">{label}</h3>
              <button type="button" onClick={() => setOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit m-3">
              {([['upload', 'Téléverser', 'ri-upload-cloud-line'], ['creations', 'Mes créations', 'ri-archive-2-line'], ['products', 'Produit', 'ri-shopping-bag-3-line']] as const).map(([k, l, ic]) => (
                <button key={k} type="button" onClick={() => setTab(k)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === k ? 'bg-background-50 text-foreground-900' : 'text-foreground-500'}`}>
                  <i className={ic}></i>{l}
                </button>
              ))}
            </div>
            <div className="px-3 pb-4 overflow-y-auto">
              {error && <div className="mb-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg"><i className="ri-error-warning-line"></i>{error}</div>}

              {tab === 'upload' && (
                <div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0] || null)} />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => fileRef.current?.click()}
                    {...dragProps}
                    className={`w-full flex flex-col items-center justify-center gap-2 py-10 rounded-xl border border-dashed transition-colors cursor-pointer ${dragging ? 'border-primary-400 bg-primary-50/60' : 'border-background-300/70 hover:border-primary-300'}`}
                  >
                    {busy ? <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i> : <i className="ri-upload-cloud-2-line text-2xl text-foreground-400"></i>}
                    <span className="text-xs text-foreground-500">{busy ? 'Téléversement…' : 'Cliquer ou glisser-déposer une image (max 6 Mo)'}</span>
                  </button>
                </div>
              )}

              {tab === 'creations' && (
                creations.length === 0 ? (
                  <p className="text-xs text-foreground-500 text-center py-10">Aucune image dans vos créations.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {creations.map((c) => (
                      <button key={c.id} type="button" onClick={() => { onChange(c.url as string); setOpen(false); }} className="aspect-square rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-400 transition-colors cursor-pointer">
                        <img src={c.url as string} alt={c.title || ''} className="w-full h-full object-cover object-top" />
                      </button>
                    ))}
                  </div>
                )
              )}

              {tab === 'products' && (
                products.length === 0 ? (
                  <p className="text-xs text-foreground-500 text-center py-10">Aucun produit Zifek disponible.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {products.map((p) => {
                      const img = firstProductImage(p.media);
                      if (!img) return null;
                      return (
                        <button key={p.id} type="button" onClick={() => { onChange(img); setOpen(false); }} className="aspect-square rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-400 transition-colors cursor-pointer" title={p.name}>
                          <img src={img} alt={p.name} className="w-full h-full object-cover object-top" />
                        </button>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}